/**
 * The boot page controller (ui-ux §6.1). The page itself is inline in `index.html` (frame 0, before any module runs):
 * a lapisNight desk, the closed codex cover with its vermilion wax seal, «МАРГИНАЛИИ», the quill progress line and a
 * scribe line. This module drives the progress (step weights: fonts 10 · save 5 · GPU 10 · pipelines 40 · bake 5 ·
 * meshes 20 · first frame 10), rotates the 12 scribe lines every 1.6 s, adds the «slow monks» line after 8 s and plays
 * the exit: the cover opens (rotateY −160°, 520 ms, perspective 1200 px); reduced motion → 150 ms crossfade.
 */
import type { TFunction } from '../shared';

export type BootStep = 'fonts' | 'save' | 'gpu' | 'pipelines' | 'bake' | 'meshes' | 'frame';
const WEIGHTS: Record<BootStep, number> = { fonts: 10, save: 5, gpu: 10, pipelines: 40, bake: 5, meshes: 20, frame: 10 };
const LINES = 12;

export class BootScreen {
  private readonly el: HTMLElement | null;
  private done = new Set<BootStep>();
  private shown = 0;
  private lineTimer: ReturnType<typeof setInterval> | null = null;
  private slowTimer: ReturnType<typeof setTimeout> | null = null;
  private line = 0;

  constructor(private readonly t: TFunction) {
    this.el = document.getElementById('boot');
    if (!this.el) return;
    this.el.setAttribute('aria-label', t('app.name'));
    const name = this.el.querySelector<HTMLElement>('.mc-boot__name');
    if (name) name.textContent = t('app.name');
    const scribe = this.el.querySelector<HTMLElement>('.mc-boot__scribe');
    this.line = Math.floor((performance.now() * 7) % LINES);
    if (scribe) scribe.textContent = t(`app.boot.line.${this.line + 1}`);
    this.lineTimer = setInterval(() => {
      this.line = (this.line + 1) % LINES;
      if (scribe) scribe.textContent = t(`app.boot.line.${this.line + 1}`);
    }, 1600);
    this.slowTimer = setTimeout(() => {
      const slow = this.el?.querySelector<HTMLElement>('.mc-boot__slow');
      if (slow) { slow.textContent = t('app.boot.slow'); slow.hidden = false; }
    }, 8000);
  }

  /** Marks a step done (skipped steps count as done: lite mode skips pipelines, meshes and the stage frame). */
  step(s: BootStep): void {
    this.done.add(s);
    let p = 0;
    for (const k of this.done) p += WEIGHTS[k];
    this.set(Math.min(100, p));
  }

  private set(p: number): void {
    if (!this.el || p <= this.shown) return;
    this.shown = p;
    this.el.style.setProperty('--p', `${p}%`);
    this.el.setAttribute('aria-valuenow', String(Math.round(p)));
    const pct = this.el.querySelector<HTMLElement>('.mc-boot__pct');
    if (pct) pct.textContent = this.t('app.boot.progress', { p: Math.round(p) });
  }

  /** Opens the cover and removes the boot page. Resolves when it is gone. */
  async exit(motion: 'full' | 'reduced' | 'min'): Promise<void> {
    if (this.lineTimer) clearInterval(this.lineTimer);
    if (this.slowTimer) clearTimeout(this.slowTimer);
    const el = this.el;
    if (!el) return;
    this.set(100);
    el.setAttribute('aria-busy', 'false');
    const cover = el.querySelector<HTMLElement>('.mc-boot__cover');
    try {
      if (motion === 'full' && cover) {
        const open = cover.animate([{ transform: 'perspective(1200px) rotateY(0deg)' }, { transform: 'perspective(1200px) rotateY(-160deg)' }],
          { duration: 520, easing: 'cubic-bezier(.55,0,.35,1)', fill: 'forwards' });
        const fade = el.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 260, delay: 340, easing: 'linear', fill: 'forwards' });
        await Promise.all([open.finished, fade.finished]);
      } else {
        await el.animate([{ opacity: 1 }, { opacity: 0 }], { duration: motion === 'min' ? 80 : 150, fill: 'forwards' }).finished;
      }
    } catch { /* animations cancelled: just remove */ }
    el.remove();
  }
}
